import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Grid,
} from "@mui/material";
import "../../App.css";
import { useChangeableItems, getLabelByKey } from "../../store/useDynamicLabel";
import dayjs from "dayjs";
import { TableNodata } from "../../components/TableNodata";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import api from "../../api/client";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { TableLoader } from "../../components/TableLoader";
import SecurityIcon from "@mui/icons-material/Security";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CustomPagination from "../../components/CustomPagination";
import useDebounce from "../../hooks/useDebounce";

const SuperAdminsList = () => {
  const changeableItems = useChangeableItems();
  const getLabel = (itemKey) => getLabelByKey(changeableItems, itemKey);

  const tableref = useRef({});
  const tableHeaders = [
    "S.No",
    "Name",
    "Email / Contact",
    "Gender / DOB",
    "City / State",
    "Created Date",
    "Status",
  ];

  const navigate = useNavigate();
  const { showSnackbar } = useSnackbar();

  // State
  const [showTable, setShowTable] = useState(false);
  const [listData, setListData] = useState([]);
  const [page, setPage] = useState(+localStorage.getItem("currentPage") || 0);
  const [rowsPerPage, setRowsPerPage] = useState(
    +localStorage.getItem("rowsPerPage") || 10
  );
  const [search, setSearch] = useState(localStorage.getItem("searchvalue") || "");
  const debouncedSearch = useDebounce(search)
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const handleAddAdmin = (isAdd, id) => {
    navigate("/addSuperAdmin", {
      state: {
        addAdmin: isAdd,
        adminID: id,
      },
    });
  };

  const fetchData = async () => {
    setLoading(true);

    const cachekey = `page_${page}_size_${rowsPerPage}_searchkey_${debouncedSearch}`;

    if (tableref.current[cachekey]) {
      const cached = tableref.current[cachekey];
      setListData(cached?.data || []);
      setTotalCount(cached?.count || 0);
      setLoading(false);
      return;
    }

    try {
      const payload = {
        page: page,
        size: rowsPerPage,
        search_key: debouncedSearch,
      };
      const res = await api.post("ListSuperAdmins", payload);

      if (res.data.status === "success") {
        const data = res.data.data;
        const count = res.data.count;

        tableref.current[cachekey] = {
          data,
          count,
        };

        setListData(res.data.data || []);
        setTotalCount(res.data.count || 0);
        setLoading(false);
      } else {
        setListData([]);
        setTotalCount(0);
        showSnackbar("error", "No super admins found");
        setLoading(false);
      }
    } catch (error) {
      console.log("Error fetching super admins:", error);
      showSnackbar("error", "Failed to fetch super admins");
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, debouncedSearch]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200); // 0.2 sec delay

    return () => clearTimeout(timer);
  }, []);

    useEffect(() => {
      localStorage.setItem("currentPage", page);
      localStorage.setItem("rowsPerPage", rowsPerPage);
    }, [page, rowsPerPage]);

  return (
    <Box sx={{ minHeight: "100vh", p: 1 }}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
      >
        <Grid
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: 0.5,
            alignItems: "center",
          }}
        >
          <Box className="icon-box">
            <SecurityIcon className="iconHeading" />
          </Box>
          <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
            {getLabel("SUPER_ADMIN")}
          </Typography>
        </Grid>
        <Box display="flex" gap={4} alignItems="center">
          <TextField
            value={search}
            onChange={(e) => {
              setPage(0);
              setSearch(e.target.value);
              localStorage.setItem("searchvalue", e.target.value);
            }}
            size="small"
            variant="outlined"
            placeholder={`Search ${getLabel("SUPER_ADMIN")}`}
            className="custom-search-field"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            className="gradient-button"
            onClick={() => handleAddAdmin("true", null)}
          >
            Add {getLabel("SUPER_ADMIN")}
          </Button>
        </Box>
      </Box>

      <TableContainer
        component={Paper}
        sx={{
          opacity: showTable ? 1 : 0,
          transform: showTable ? "translateY(0)" : "translateY(40px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              {tableHeaders.map((header) => (
                <TableCell key={header} className="tableHeader">
                  <b>{header}</b>
                </TableCell>
              ))}
              <TableCell className="tableHeader">
                <b>Actions</b>
              </TableCell>
            </TableRow>
          </TableHead>
          {loading ? (
            <TableCell colSpan={tableHeaders.length + 1} align="center">
              <TableLoader message="Fetching super admins..." />
            </TableCell>
          ) : (
            <TableBody className="tablebody">
              {listData.map((item, index) => (
                <TableRow
                  key={item.user_id}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                    "&:hover": { backgroundColor: "#f1f1f1" },
                  }}
                >
                  <TableCell align="center">
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell>
                    {item.first_name} {item.last_name}
                  </TableCell>
                  <TableCell>
                    {item.email} <br /> {item.phone}
                  </TableCell>
                  <TableCell>
                    {item.gender} <br />{" "}
                    {item.dob ? dayjs(item.dob).format("DD-MM-YYYY") : "-"}
                  </TableCell>
                  <TableCell>
                    {item.city} <br /> {item.state} <br /> {item.pincode}
                  </TableCell>
                  <TableCell>
                    {item.created_at
                      ? dayjs(item.created_at).format("DD-MM-YYYY")
                      : "-"}
                  </TableCell>

                  <TableCell>
                    {" "}
                    <Box
                      sx={{
                        display: "inline-block",
                        px: 2,
                        py: 0.5,
                        borderRadius: "12px",
                        fontWeight: "bold",
                        fontSize: "12px",
                        backgroundColor:
                          item.status === "ACTIVE"
                            ? "#d4edda"
                            : item.status === "INACTIVE"
                            ? "#f8d7da"
                            : "#f1f1f1",
                        color:
                          item.status === "ACTIVE"
                            ? "#155724"
                            : item.status === "INACTIVE"
                            ? "#721c24"
                            : "#333",
                        textAlign: "center",
                      }}
                    >
                      {item.status}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {/* <Tooltip
                      title="View"
                      arrow
                      componentsProps={{
                        tooltip: {
                          sx: {
                            bgcolor: "primary.main",
                            color: "white",
                            fontSize: 13,
                            fontWeight: 500,
                          },
                        },
                        arrow: {
                          sx: {
                            color: "primary.main",
                          },
                        },
                      }}
                    > */}
                    <IconButton
                      color="primary"
                      // onClick={() => handleAddAdmin(false, item.user_id)}
                      onClick={() =>
                        navigate("/viewSuperAdmin", {
                          state: {
                            adminID: item.user_id,
                          },
                        })
                      }
                      sx={{
                        display: "flex",
                        // alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <VisibilityIcon />
                      <Typography color="primary" sx={{ fontSize: "12px" }}>
                        View
                      </Typography>
                    </IconButton>
                    {/* </Tooltip> */}
                  </TableCell>
                </TableRow>
              ))}

              {((page === 0 && listData?.length > rowsPerPage - 1) ||
                page !== 0) && (
                <CustomPagination
                  page={page}
                  rowsPerPage={rowsPerPage}
                  count={totalCount}
                  setPage={setPage}
                  colSpan={tableHeaders.length + 1}
                />
              )}

              {listData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length + 1} align="center">
                    <TableNodata />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default SuperAdminsList;
